import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isCurrentUserEmailVerified } from '../../services/auth';
import { InviteShell } from '../auth/AcceptInviteLayout';
import type { SignupLookupResponse, SignupSport } from '../../types/signup';
import { SignupEmailStep } from './SignupEmailStep';
import {
  SignupAccountStep,
  type SignupAccountCreated,
  type SignupAccountInitialValues,
} from './SignupAccountStep';
import { SignupVerifyStep } from './SignupVerifyStep';
import { SignupPlanStep } from './SignupPlanStep';
import { SignupOutcome, type SignupOutcomeState } from './SignupOutcome';
import { clearSignup, loadSignup, saveSignup } from './signupStorage';
import { pendingSignupPath } from '../../utils/profileMissing';

/**
 * Public self-serve signup: email lookup, account, email verification, then into the app.
 *
 * Public on purpose, like `/accept-invite`: the coach has no account when they arrive. No price or
 * school tier is chosen here. The lookup reports what the server derived from the email domain, and
 * the create call sends a name, a sport and, for a sport priced by plan (baseball), the plan KEY the
 * coach picked; the server prices it.
 *
 * Payment is not a step of this page. A new program starts its free trial, so the coach goes
 * straight into the app; the trial banner links to `/signup/payment` (also Stripe's cancel URL, and
 * where an admin is sent once the trial ends unpaid), which owns the checkout.
 */

type SignupView =
  | { phase: 'email'; email?: string }
  | {
      phase: 'account';
      email: string;
      lookup: SignupLookupResponse;
      /** The form as it was, when the coach is signing back in to a signup already under way. */
      initialValues?: SignupAccountInitialValues;
    }
  | {
      phase: 'verify';
      email: string;
      lookup: SignupLookupResponse;
      firstName: string;
      lastName: string;
      sport: SignupSport;
      planTier?: string;
      /** The school name typed when the lookup did not match one. */
      schoolName?: string;
      acceptedTerms: true;
      alreadyVerified: boolean;
      verificationSendFailed: boolean;
    }
  | {
      /** The create call refused the plan (`invalid_plan_tier`): pick again, then back to verify. */
      phase: 'plan';
      email: string;
      lookup: SignupLookupResponse;
      firstName: string;
      lastName: string;
      sport: SignupSport;
      planTier?: string;
      schoolName?: string;
      acceptedTerms: true;
      message: string;
    }
  | { phase: 'outcome'; outcome: SignupOutcomeState };

export const SignupPage = () => {
  const { user, logout, organizationAwaitingPayment } = useAuth();

  /**
   * A refresh on the verify step (or a second tab, since the form is kept in localStorage) resumes
   * it, but only for the Firebase account it was started with and only while that account has no
   * RosterPilot profile yet. Read during the first render, when auth has already settled
   * (`AppRoutes` renders no route before it does).
   */
  const [resumed] = useState(() =>
    user && !user.profile ? loadSignup(user.email) : null
  );
  /**
   * A travel ball signup under way for this account (the verification page's Continue, an old link
   * or the login banner can land a travel ball coach here). It goes back to its own page, and is
   * never signed out below: the session is shared by every tab, so that would also end the signup
   * in the tab still waiting on it.
   */
  const [travelBallSignupPath] = useState(() =>
    user && !user.profile && !resumed ? pendingSignupPath(user.email) : null
  );
  const [view, setView] = useState<SignupView>(() =>
    resumed
      ? {
          phase: 'verify',
          email: resumed.email,
          lookup: resumed.lookup,
          firstName: resumed.firstName,
          lastName: resumed.lastName,
          sport: resumed.sport,
          planTier: resumed.planTier,
          schoolName: resumed.schoolName,
          acceptedTerms: resumed.acceptedTerms,
          // Verified in another tab (or before a refresh): nothing to wait for, so the step checks
          // straight away instead of asking for another click.
          alreadyVerified: isCurrentUserEmailVerified(),
          // Nothing is known about the last send; the cooldown is the safe default.
          verificationSendFailed: false,
        }
      : // A Firebase-only session we cannot resume is signed out below; keep its address so the
        // coach can pick the signup up again (the account step signs back in to that account).
        { phase: 'email', email: user && !user.profile ? (user.email ?? undefined) : undefined }
  );

  /**
   * A Firebase-only session that is not a signup we can resume is signed out, as AcceptInvite does:
   * through AuthContext, so per-user caches are cleared too. A coach coming back to an abandoned
   * signup is picked up again by the account step when they re-enter the same email and password.
   * A session we *can* resume (this tab's refresh, or another tab's signup) is left alone: signing
   * it out would sign out every tab.
   *
   * Mount only: after this, the page itself creates the session it would otherwise be signing out.
   */
  useEffect(() => {
    if (user && !user.profile && !resumed && !travelBallSignupPath) {
      clearSignup();
      void logout().catch(() => {
        // Creating the new account replaces the session anyway.
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startOver = useCallback(() => {
    clearSignup();
    if (user) {
      void logout().catch(() => {
        // A stale session is replaced by the next account creation.
      });
    }
    setView({ phase: 'email' });
  }, [user, logout]);

  const handleLookup = (email: string, lookup: SignupLookupResponse) => {
    switch (lookup.status) {
      case 'eligible':
        setView({ phase: 'account', email, lookup });
        return;
      case 'unverified':
        setView({ phase: 'outcome', outcome: { phase: 'unverified' } });
        return;
      default:
        setView({ phase: 'outcome', outcome: { phase: 'unavailable' } });
    }
  };

  const handleCreated = (email: string, lookup: SignupLookupResponse, created: SignupAccountCreated) => {
    saveSignup({
      step: 'verify',
      email,
      firstName: created.firstName,
      lastName: created.lastName,
      sport: created.sport,
      ...(created.planTier ? { planTier: created.planTier } : {}),
      ...(created.schoolName ? { schoolName: created.schoolName } : {}),
      acceptedTerms: created.acceptedTerms,
      lookup,
    });
    setView({ phase: 'verify', email, lookup, ...created });
  };

  /** A new plan picked after the server refused the last one: store it, then create again. */
  const handlePlanChosen = (
    from: Extract<SignupView, { phase: 'plan' }>,
    lookup: SignupLookupResponse,
    planTier: string | undefined
  ) => {
    saveSignup({
      step: 'verify',
      email: from.email,
      firstName: from.firstName,
      lastName: from.lastName,
      sport: from.sport,
      ...(planTier ? { planTier } : {}),
      ...(from.schoolName ? { schoolName: from.schoolName } : {}),
      acceptedTerms: from.acceptedTerms,
      lookup,
    });
    setView({
      phase: 'verify',
      email: from.email,
      lookup,
      firstName: from.firstName,
      lastName: from.lastName,
      sport: from.sport,
      planTier,
      schoolName: from.schoolName,
      acceptedTerms: from.acceptedTerms,
      // Verified already (the create call is only made after that), so the step creates straight away.
      alreadyVerified: isCurrentUserEmailVerified(),
      verificationSendFailed: false,
    });
  };

  // Someone who is already a RosterPilot user has nothing to sign up for: an unpaid admin goes to
  // payment, anyone else into the app, the same way `/login` bounces a signed-in user.
  if (user?.profile) {
    return <Navigate to={organizationAwaitingPayment ? '/signup/payment' : '/players'} replace />;
  }

  if (travelBallSignupPath) {
    return <Navigate to={travelBallSignupPath} replace />;
  }

  return (
    <InviteShell>
      {view.phase === 'email' && (
        <SignupEmailStep initialEmail={view.email} onResult={handleLookup} />
      )}

      {view.phase === 'account' && (
        <SignupAccountStep
          email={view.email}
          lookup={view.lookup}
          onCreated={(created) => handleCreated(view.email, view.lookup, created)}
          initialValues={view.initialValues}
          onEmailInUse={() =>
            setView({
              phase: 'outcome',
              outcome: {
                phase: 'signInInstead',
                email: view.email,
                message: null,
                passwordHelp: true,
              },
            })
          }
          onChangeEmail={() => setView({ phase: 'email', email: view.email })}
        />
      )}

      {view.phase === 'verify' && (
        <SignupVerifyStep
          email={view.email}
          firstName={view.firstName}
          lastName={view.lastName}
          sport={view.sport}
          planTier={view.planTier}
          schoolName={view.schoolName}
          acceptedTerms={view.acceptedTerms}
          alreadyVerified={view.alreadyVerified}
          verificationSendFailed={view.verificationSendFailed}
          onOutcome={(outcome) => setView({ phase: 'outcome', outcome })}
          onPlanRejected={(message) =>
            setView({
              phase: 'plan',
              email: view.email,
              lookup: view.lookup,
              firstName: view.firstName,
              lastName: view.lastName,
              sport: view.sport,
              planTier: view.planTier,
              schoolName: view.schoolName,
              acceptedTerms: view.acceptedTerms,
              message,
            })
          }
          onSignInAgain={(schoolNameError) =>
            setView({
              phase: 'account',
              email: view.email,
              lookup: view.lookup,
              initialValues: {
                firstName: view.firstName,
                lastName: view.lastName,
                sport: view.sport,
                planTier: view.planTier,
                schoolName: view.schoolName,
                ...(schoolNameError ? { schoolNameError } : {}),
              },
            })
          }
          onStartOver={startOver}
        />
      )}

      {view.phase === 'plan' && (
        <SignupPlanStep
          email={view.email}
          lookup={view.lookup}
          sport={view.sport}
          message={view.message}
          onContinue={(lookup, planTier) => handlePlanChosen(view, lookup, planTier)}
          onStartOver={startOver}
        />
      )}

      {view.phase === 'outcome' && <SignupOutcome outcome={view.outcome} onStartOver={startOver} />}
    </InviteShell>
  );
};
